<template>
  <div v-if="!isSharedDashboard">
    <!-- Mobile Header -->
    <div
      class="fixed top-0 right-0 left-0 z-50 flex h-14 items-center justify-between bg-burgundy-50 px-4 lg:hidden"
    >
      <button
        class="flex items-center justify-center rounded-md p-2 text-burgundy-700 hover:bg-burgundy-900/5 hover:text-burgundy-900"
        @click="mobileMenuOpen = !mobileMenuOpen"
      >
        <Icon :name="mobileMenuOpen ? 'times' : 'bars'" class="size-6" />
      </button>
      <RouterLink
        :to="{ name: 'base' }"
        aria-label="Home"
        class="flex items-center gap-2"
      >
        <img src="@/assets/fyr-logo.svg" alt="Fyr" class="h-auto w-[54.5px]" />
      </RouterLink>
      <!-- Spacer keeps the logo centered; notifications live in the
           right-edge sidepanel stack, same as every other width. -->
      <div class="size-10" aria-hidden="true" />
    </div>

    <!-- Mobile Navigation Drawer -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="mobileMenuOpen"
        class="fixed inset-y-0 left-0 z-40 w-72 overflow-y-auto bg-burgundy-50 pt-16 shadow-xl lg:hidden"
      >
        <div class="flex flex-col space-y-1 p-4">
          <!-- Main Nav Items (Mobile) -->
          <template v-for="item in mobileNavbarRoutes" :key="item.name">
            <button
              v-if="!item.meta?.isDirect"
              class="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm text-burgundy-700 hover:bg-burgundy-900/5 hover:text-burgundy-900"
              :class="{
                'text-purple-900': mobileActiveMenu === item.meta?.title,
              }"
              @click="
                mobileActiveMenu =
                  mobileActiveMenu === item.meta?.title
                    ? null
                    : (item.meta?.title ?? null)
              "
            >
              <span class="flex items-center gap-3">
                <Icon :name="item.meta?.icon" class="size-5" />
                {{ item.meta?.text ?? item.meta?.title ?? item.name }}
              </span>
              <Icon
                name="chevron-down"
                class="size-4 transition-transform"
                :class="{ 'rotate-180': mobileActiveMenu === item.meta?.title }"
              />
            </button>
            <RouterLink
              v-else
              :to="{ name: item.name }"
              class="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-burgundy-700 hover:bg-burgundy-900/5 hover:text-burgundy-900"
              @click="mobileMenuOpen = false"
            >
              <Icon :name="item.meta?.icon" class="size-5" />
              {{ item.meta?.text ?? item.meta?.title ?? item.name }}
            </RouterLink>

            <!-- Submenu Items (Mobile) -->
            <Transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 -translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 -translate-y-2"
            >
              <div
                v-if="mobileActiveMenu === item.meta?.title && item.children"
                class="ml-[22px] flex flex-col space-y-1 border-l border-burgundy-900/10 pl-3"
              >
                <RouterLink
                  v-for="child in getAccordionChildren(
                    String(item.meta?.title),
                  )"
                  :key="child.href"
                  :to="{ name: child.href }"
                  class="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-burgundy-600 hover:bg-burgundy-900/5 hover:text-burgundy-900"
                  @click="mobileMenuOpen = false"
                >
                  {{ child.name }}
                  <BetaBadge v-if="child.beta" size="sm" />
                </RouterLink>
              </div>
            </Transition>
          </template>

          <!-- Settings (Mobile) -->
          <div class="mt-4 border-t border-burgundy-900/10 pt-4">
            <button
              class="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-sm text-burgundy-700 hover:bg-burgundy-900/5 hover:text-burgundy-900"
              :class="{
                'text-purple-900': mobileActiveMenu === 'Settings',
              }"
              @click="
                mobileActiveMenu =
                  mobileActiveMenu === 'Settings' ? null : 'Settings'
              "
            >
              <span class="flex items-center gap-3">
                <Icon name="settings" class="size-5" />
                Settings
              </span>
              <Icon
                name="chevron-down"
                class="size-4 transition-transform"
                :class="{ 'rotate-180': mobileActiveMenu === 'Settings' }"
              />
            </button>
            <Transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="opacity-0 -translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 -translate-y-2"
            >
              <div
                v-if="mobileActiveMenu === 'Settings'"
                class="ml-[22px] flex flex-col space-y-1 border-l border-burgundy-900/10 pl-3"
              >
                <RouterLink
                  v-for="tab in settingsTabs"
                  :key="tab.href"
                  :to="{ name: tab.href }"
                  class="rounded-md px-3 py-2 text-sm text-burgundy-600 hover:bg-burgundy-900/5 hover:text-burgundy-900"
                  @click="mobileMenuOpen = false"
                >
                  {{ tab.name }}
                </RouterLink>
              </div>
            </Transition>

            <div class="mt-4 border-t border-burgundy-900/10 pt-4">
              <div class="mb-2 flex items-center justify-between px-2">
                <p class="text-xs text-burgundy-400">Organization</p>
                <button
                  class="rounded-md p-1 text-burgundy-400 hover:bg-burgundy-900/5 hover:text-burgundy-900"
                  @click="mobileOrgSearchOpen = !mobileOrgSearchOpen"
                >
                  <Icon name="search" class="size-4" />
                </button>
              </div>
              <Transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 -translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-1"
              >
                <div v-if="mobileOrgSearchOpen" class="mb-2 px-2">
                  <input
                    v-model="mobileOrgSearchQuery"
                    type="text"
                    class="w-full rounded-md border border-burgundy-900/15 bg-white px-2 py-1.5 text-sm text-burgundy-900 placeholder-burgundy-400 focus:border-purple-400 focus:ring-1 focus:ring-purple-300 focus:outline-none"
                    placeholder="Search organizations..."
                    @click.stop
                  />
                </div>
              </Transition>
              <div class="max-h-60 overflow-y-auto">
                <button
                  v-for="org in mobileFilteredOrganizations"
                  :key="org.id"
                  class="flex w-full items-center gap-2 rounded-md p-2 text-sm"
                  :class="
                    org.id === currentOrganization?.id
                      ? 'text-purple-900'
                      : 'text-burgundy-700 hover:bg-burgundy-900/5'
                  "
                  @click="selectFromMobileMenu(org.id)"
                >
                  <Icon
                    v-if="org.id === currentOrganization?.id"
                    name="check"
                    class="size-4"
                  />
                  <span v-else class="size-4" />
                  <span class="truncate">{{ org.name }}</span>
                </button>
                <div
                  v-if="mobileFilteredOrganizations.length === 0"
                  class="p-2 text-xs text-burgundy-400"
                >
                  No matches
                </div>
              </div>
            </div>

            <a
              :href="HELP_CENTER_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-burgundy-700 hover:bg-burgundy-900/5 hover:text-burgundy-900"
              @click="handleHelpCenterClick"
            >
              <Icon name="book-open-lucide" class="size-5" />
              Help Center
              <Icon
                name="arrow-up-right-from-square"
                class="ml-auto size-3 text-burgundy-400"
              />
            </a>

            <button
              v-if="pageTourKey"
              type="button"
              class="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-burgundy-700 hover:bg-burgundy-900/5 hover:text-burgundy-900"
              @click="startPageTourFromMenu"
            >
              <Icon name="compass-lucide" class="size-5" />
              Page tour
              <span
                v-if="hasUnseenPageTour"
                class="ml-auto rounded-full bg-purple-800 px-1.5 py-0.5 text-[10px] font-bold text-white"
              >
                NEW
              </span>
            </button>

            <!-- Logout (Mobile) -->
            <button
              class="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-burgundy-700 hover:bg-burgundy-900/5 hover:text-burgundy-900"
              @click="handleLogout"
            >
              <Icon name="arrow-right-from-bracket-light" class="size-5" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Mobile Backdrop -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="mobileMenuOpen"
        class="fixed inset-0 z-30 bg-black/50 lg:hidden"
        @click="mobileMenuOpen = false"
      />
    </Transition>

    <!-- Desktop Sidebar -->
    <div id="base" class="fixed top-0 left-0 z-50 hidden h-screen lg:block">
      <nav class="flex h-full bg-burgundy-50">
        <!-- LEFT SIDEBAR + SUBMENU WRAPPER -->
        <div class="flex h-full shrink-0">
          <!-- Sidebar -->
          <div
            class="group/sidebar relative flex h-full flex-col transition-all duration-200"
            :style="{ width: `${sidebarVisualWidth}px` }"
          >
            <div
              class="mb-2 flex h-13 shrink-0 items-center border-b border-burgundy-900/10"
            >
              <RouterLink
                :to="{ name: 'base' }"
                aria-label="Home"
                class="mx-2 flex h-11 w-full items-center rounded-md focus-visible:outline-2 focus-visible:outline-purple-800"
                :class="isExpanded ? 'px-2' : 'justify-center'"
              >
                <img
                  v-if="isExpanded"
                  src="@/assets/fyr-logo.svg"
                  alt="Fyr"
                  class="h-auto w-[54.5px]"
                />
                <Icon
                  v-else
                  name="fyr-logo"
                  class="h-7 w-[26px] text-fyr-dark"
                />
              </RouterLink>
            </div>

            <SidebarToggle
              v-if="!hasApplicationHeader"
              class="mb-2 h-10 shrink-0 self-center rounded-md border-r-0"
            />

            <!-- Middle nav icons -->
            <div
              class="custom-sidebar-scroll flex min-h-0 flex-1 flex-col overflow-y-auto"
            >
              <div
                v-if="authStore.isAuthed"
                class="hidden w-full lg:flex lg:flex-col lg:space-y-3"
              >
                <div
                  class="flex w-full flex-col space-y-2"
                  :class="{ 'p-2': isExpanded }"
                >
                  <!-- Main nav -->
                  <div
                    v-if="authStore.isAuthed"
                    class="flex w-full flex-col justify-center space-y-2"
                    :class="{ '!mt-4': !isExpanded }"
                  >
                    <!-- Hub (agency users only) -->
                    <button
                      v-if="isCurrentAgencyUser"
                      v-tooltip.right="
                        !isExpanded
                          ? disabledRouteNames.has('hub')
                            ? 'Hub is disabled for this organization'
                            : 'Hub'
                          : ''
                      "
                      :disabled="disabledRouteNames.has('hub')"
                      aria-label="Hub"
                      class="flex w-full items-center text-burgundy-900 transition-all duration-300 ease-in-out"
                      :class="[
                        navTextClass,
                        disabledRouteNames.has('hub')
                          ? 'cursor-not-allowed opacity-40'
                          : {
                              'text-purple-800':
                                !isExpanded && currentRoute.name === 'hub',
                              'text-purple-900':
                                isExpanded && currentRoute.name === 'hub',
                              'bg-burgundy-100':
                                !isExpanded && currentRoute.name === 'hub',
                              'hover:rounded-md hover:bg-burgundy-900/5 hover:text-burgundy-900':
                                currentRoute.name !== 'hub',
                            },
                        {
                          'gap-2 p-2': isExpanded,
                          'justify-center p-2': !isExpanded,
                        },
                      ]"
                      @click="router.push({ name: 'hub' })"
                    >
                      <Icon
                        name="users-round-lucide"
                        class="size-5 shrink-0"
                        :class="{
                          'stroke-2':
                            !isExpanded && currentRoute.name === 'hub',
                        }"
                      />
                      <span
                        v-if="isExpanded"
                        class="font-arial flex-1 truncate text-left"
                        :class="
                          currentRoute.name === 'hub'
                            ? 'font-medium'
                            : 'font-normal'
                        "
                      >
                        Hub
                      </span>
                    </button>
                    <template
                      v-for="item in visibleNavbarRoutes"
                      :key="item.name"
                    >
                      <button
                        v-tooltip.right="
                          !isExpanded
                            ? disabledRouteNames.has(String(item.name))
                              ? `${navItemLabel(item)} is disabled for this organization`
                              : navItemLabel(item)
                            : ''
                        "
                        :disabled="disabledRouteNames.has(String(item.name))"
                        :aria-label="
                          isExpanded ? undefined : navItemLabel(item)
                        "
                        :aria-expanded="
                          item.meta?.isDirect || !isExpanded
                            ? undefined
                            : isMenuOpen(item.meta?.title)
                        "
                        class="flex w-full items-center text-burgundy-900 transition-all duration-300 ease-in-out"
                        :class="[
                          navTextClass,
                          {
                            'text-purple-800': isCollapsedActive(item),
                            'text-purple-900':
                              (isParentActive(item) ||
                                isClosedFolderActive(item)) &&
                              !isCollapsedActive(item),
                            'bg-burgundy-100': isCollapsedActive(item),
                            'hover:rounded-md hover:bg-burgundy-900/5 hover:text-burgundy-900':
                              !isParentActive(item) &&
                              !isClosedFolderActive(item) &&
                              !isCollapsedActive(item) &&
                              !disabledRouteNames.has(String(item.name)),
                            'cursor-not-allowed opacity-40':
                              disabledRouteNames.has(String(item.name)),
                            'gap-2 p-2': isExpanded,
                            'justify-center p-2': !isExpanded,
                          },
                        ]"
                        @click="handleMenuClick(item)"
                      >
                        <Icon
                          :name="item.meta?.icon"
                          class="size-5 shrink-0"
                          :class="{ 'stroke-2': isCollapsedActive(item) }"
                        />
                        <span
                          v-if="isExpanded"
                          class="font-arial flex-1 truncate text-left"
                          :class="
                            isParentActive(item) || isFolderActive(item)
                              ? 'font-medium'
                              : 'font-normal'
                          "
                          >{{ navItemLabel(item) }}</span
                        >
                        <BetaBadge
                          v-if="isExpanded && item.meta?.beta"
                          size="sm"
                        />
                        <Icon
                          v-if="isExpanded && !item.meta?.isDirect"
                          name="chevron-down"
                          class="mr-1 size-3 shrink-0 transition-transform duration-200"
                          :class="{
                            'rotate-180': isMenuOpen(item.meta?.title),
                          }"
                        />
                      </button>
                      <Transition
                        enter-active-class="transition duration-200 ease-out"
                        enter-from-class="opacity-0 -translate-y-2"
                        enter-to-class="opacity-100 translate-y-0"
                      >
                        <div
                          v-if="
                            isExpanded &&
                            isMenuOpen(item.meta?.title) &&
                            !item.meta?.isDirect &&
                            getAccordionChildren(String(item.meta?.title))
                              .length > 0
                          "
                          class="ml-4.5 flex flex-col space-y-0.5 border-l border-burgundy-900/10 pl-3"
                        >
                          <template
                            v-for="child in getAccordionChildren(
                              String(item.meta?.title),
                            )"
                            :key="child.href"
                          >
                            <button
                              v-if="getSubChildren(child.href).length > 0"
                              :aria-expanded="isSubmenuOpen(child.href)"
                              :class="[
                                subNavItemClass,
                                navSubTextClass,
                                {
                                  'text-purple-900': isChildActive(child.href),
                                },
                              ]"
                              @click="toggleSubmenu(child.href)"
                            >
                              <span class="flex items-center gap-1.5">
                                {{ child.name }}
                                <BetaBadge v-if="child.beta" size="sm" />
                              </span>
                              <Icon
                                name="chevron-down"
                                class="size-2.5 shrink-0 transition-transform duration-200"
                                :class="{
                                  'rotate-180': isSubmenuOpen(child.href),
                                }"
                              />
                            </button>
                            <RouterLink
                              v-else
                              :to="{ name: child.href }"
                              :class="[
                                subNavItemClass,
                                navSubTextClass,
                                {
                                  'text-purple-900': isChildActive(child.href),
                                },
                              ]"
                            >
                              <span class="flex items-center gap-1.5">
                                {{ child.name }}
                                <BetaBadge v-if="child.beta" size="sm" />
                              </span>
                            </RouterLink>
                            <div
                              v-if="
                                getSubChildren(child.href).length > 0 &&
                                isSubmenuOpen(child.href)
                              "
                              class="ml-4 flex flex-col space-y-0.5 border-l border-burgundy-900/10 pl-0"
                            >
                              <RouterLink
                                v-if="hasOverrideForChild(child.href)"
                                :to="submenuOverride!.backLink!.to"
                                class="font-arial flex items-center gap-1 rounded-md px-2 py-1.5 font-normal text-burgundy-400 hover:text-burgundy-900"
                                :class="navSubTextClass"
                              >
                                <Icon
                                  name="long-arrow-right"
                                  class="size-3 rotate-180"
                                />
                                {{ submenuOverride!.backLink!.label }}
                              </RouterLink>
                              <RouterLink
                                v-for="sub in getSubChildren(child.href)"
                                :key="sub.href"
                                :to="
                                  sub.query
                                    ? {
                                        name: sub.href,
                                        query: sub.query,
                                      }
                                    : { name: sub.href }
                                "
                                class="font-arial flex items-center gap-1.5 rounded-md px-3 py-1.5 font-normal text-burgundy-600 hover:bg-burgundy-900/5 hover:text-burgundy-900"
                                :class="[
                                  navSubTextClass,
                                  {
                                    'text-purple-900':
                                      currentRoute.name === sub.href ||
                                      currentRoute.matched.some(
                                        (m) => m.name === sub.href,
                                      ),
                                  },
                                ]"
                              >
                                {{ sub.name }}
                                <BetaBadge v-if="sub.beta" size="sm" />
                              </RouterLink>
                            </div>
                          </template>
                        </div>
                      </Transition>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="authStore.isAuthed"
              class="mt-auto flex w-full shrink-0 flex-col"
            >
              <div
                class="mb-1 flex w-full flex-col border-t border-burgundy-900/10 pt-2"
                :class="{ 'px-2': isExpanded }"
              >
                <a
                  v-tooltip.right="isExpanded ? '' : 'Help Center'"
                  :href="HELP_CENTER_URL"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="isExpanded ? undefined : 'Help Center'"
                  class="mb-1 flex w-full items-center gap-2.5 rounded-md py-1.5 text-burgundy-700 transition-colors duration-200 hover:bg-burgundy-900/5 hover:text-burgundy-900 focus-visible:bg-burgundy-900/5 focus-visible:outline-none"
                  :class="isExpanded ? 'px-2' : 'justify-center px-1.5'"
                  @click="handleHelpCenterClick"
                >
                  <span
                    class="flex size-7 shrink-0 items-center justify-center"
                  >
                    <Icon name="book-open-lucide" class="size-4" />
                  </span>
                  <span
                    v-if="isExpanded"
                    class="font-arial min-w-0 flex-1 truncate text-sm leading-tight font-normal"
                  >
                    Help Center
                  </span>
                  <Icon
                    v-if="isExpanded"
                    name="arrow-up-right-from-square"
                    class="mr-1 size-3 shrink-0 text-burgundy-400"
                  />
                </a>

                <button
                  v-if="pageTourKey"
                  v-tooltip.right="isExpanded ? '' : 'Page tour'"
                  type="button"
                  :aria-label="
                    isExpanded
                      ? undefined
                      : `Page tour${hasUnseenPageTour ? ' (new)' : ''}`
                  "
                  class="mb-1 flex w-full items-center gap-2.5 rounded-md py-1.5 text-burgundy-700 transition-colors duration-200 hover:bg-burgundy-900/5 hover:text-burgundy-900 focus-visible:bg-burgundy-900/5 focus-visible:outline-none"
                  :class="isExpanded ? 'px-2' : 'justify-center px-1.5'"
                  @click="guidedTour.start(pageTourKey)"
                >
                  <span
                    class="relative flex size-7 shrink-0 items-center justify-center"
                  >
                    <Icon name="compass-lucide" class="size-4" />
                    <!-- Page tours never start by themselves, so a page with
                         one the user hasn't taken says so here. -->
                    <span
                      v-if="hasUnseenPageTour"
                      aria-hidden="true"
                      class="absolute top-0.5 right-0.5 size-2 rounded-full bg-purple-800 ring-2 ring-white"
                    />
                  </span>
                  <span
                    v-if="isExpanded"
                    class="font-arial min-w-0 flex-1 truncate text-left text-sm leading-tight font-normal"
                  >
                    Page tour
                    <span v-if="hasUnseenPageTour" class="sr-only">
                      (new)
                    </span>
                  </span>
                </button>

                <div class="mb-1 flex shrink-0 items-center">
                  <Popover v-slot="{ open, close }" class="relative w-full">
                    <PopoverButton as="template">
                      <button
                        ref="organizationSelectorReference"
                        v-tooltip.right="
                          !isExpanded
                            ? (currentOrganization?.name ?? 'Organization')
                            : ''
                        "
                        aria-label="Switch organization"
                        class="flex !w-full items-center gap-2.5 rounded-md py-1.5 text-xs text-burgundy-900 transition-colors duration-200 hover:bg-burgundy-900/5 hover:text-burgundy-900 focus-visible:bg-burgundy-900/5 focus-visible:outline-none"
                        :class="{
                          'px-2': isExpanded,
                          'justify-center px-1.5': !isExpanded,
                        }"
                      >
                        <img
                          v-if="currentOrganization?.iconUrl"
                          :src="currentOrganization.iconUrl"
                          :alt="currentOrganization.name"
                          class="size-7 shrink-0 rounded-md object-cover"
                        />
                        <div
                          v-else
                          class="flex size-7 shrink-0 items-center justify-center rounded-md bg-burgundy-900/5 text-burgundy-700"
                        >
                          <Icon name="building-light" class="size-4" />
                        </div>
                        <div v-if="isExpanded" class="min-w-0 flex-1">
                          <p
                            class="font-arial truncate text-left text-sm leading-tight font-medium text-burgundy-900"
                          >
                            {{ currentOrganization?.name ?? "Organization" }}
                          </p>
                        </div>
                        <Icon
                          v-if="isExpanded"
                          name="chevron-down"
                          class="mr-1 size-3 shrink-0 text-burgundy-400 transition-transform duration-200"
                          :class="{ 'rotate-180': !open }"
                        />
                      </button>
                    </PopoverButton>
                    <Teleport to="body">
                      <div
                        ref="organizationSelectorFloating"
                        :style="organizationSelectorStyle"
                      >
                        <Transition
                          enter-active-class="transition duration-150 ease-out"
                          enter-from-class="scale-95 opacity-0"
                          enter-to-class="scale-100 opacity-100"
                          leave-active-class="transition duration-100 ease-in"
                          leave-from-class="scale-100 opacity-100"
                          leave-to-class="scale-95 opacity-0"
                        >
                          <PopoverPanel
                            class="flex max-h-[calc(100dvh-10rem)] w-64 origin-bottom-left flex-col overflow-hidden rounded-xl bg-white shadow-[0_0_16px_rgba(0,0,0,0.12),0_20px_25px_-5px_rgba(0,0,0,0.12)] ring-1 ring-burgundy-900/20"
                          >
                            <OrganizationSwitcherList @selected="close()" />
                          </PopoverPanel>
                        </Transition>
                      </div>
                    </Teleport>
                  </Popover>
                </div>

                <!-- Account card (expanded) -->
                <div
                  v-if="isExpanded"
                  class="group/account flex items-center gap-2 rounded-md p-1.5 transition-colors duration-200 hover:bg-burgundy-900/5"
                >
                  <RouterLink
                    v-tooltip.top="'Profile settings'"
                    :to="{ name: 'profile' }"
                    aria-label="Profile settings"
                    class="group/avatar relative shrink-0 rounded-full"
                  >
                    <Avatar
                      :user="self.data"
                      size="size-8"
                      font-size="text-[11px]"
                    />
                    <!-- Hover layover: avatar dims behind a cog to signal the
                         profile-settings link -->
                    <span
                      class="absolute inset-0 flex items-center justify-center rounded-full bg-burgundy-900/60 text-white opacity-0 transition-opacity duration-200 group-hover/avatar:opacity-100 group-focus-visible/avatar:opacity-100"
                    >
                      <Icon name="cog" class="size-4" />
                    </span>
                  </RouterLink>
                  <div class="min-w-0 flex-1">
                    <p
                      class="font-arial truncate text-xs leading-tight font-medium text-burgundy-900"
                    >
                      {{ self.data?.name }}
                    </p>
                    <p
                      class="font-arial truncate text-[11px] leading-tight font-normal text-burgundy-400"
                    >
                      {{ self.data?.email }}
                    </p>
                  </div>
                  <button
                    v-tooltip.top="'Logout'"
                    aria-label="Logout"
                    class="shrink-0 rounded-md p-1.5 text-burgundy-400 transition-colors duration-200 hover:bg-burgundy-900/10 hover:text-burgundy-900 focus-visible:text-burgundy-900 focus-visible:outline-none"
                    @click="handleLogout"
                  >
                    <Icon
                      name="arrow-right-from-bracket-light"
                      class="size-4"
                    />
                  </button>
                </div>

                <!-- Account stack (collapsed) -->
                <div v-else class="flex flex-col items-center gap-1">
                  <RouterLink
                    v-tooltip.right="'Profile settings'"
                    :to="{ name: 'profile' }"
                    aria-label="Profile settings"
                    class="group/avatar relative p-1"
                  >
                    <Avatar
                      :user="self.data"
                      size="size-7"
                      font-size="text-[10px]"
                    />
                    <!-- Hover layover: avatar dims behind a cog to signal the
                         profile-settings link -->
                    <span
                      class="absolute inset-1 flex items-center justify-center rounded-full bg-burgundy-900/60 text-white opacity-0 transition-opacity duration-200 group-hover/avatar:opacity-100 group-focus-visible/avatar:opacity-100"
                    >
                      <Icon name="cog" class="size-3.5" />
                    </span>
                  </RouterLink>
                  <button
                    v-tooltip.right="'Logout'"
                    aria-label="Logout"
                    class="rounded-md p-2 text-burgundy-600 transition-colors duration-200 hover:bg-burgundy-900/5 hover:text-burgundy-900"
                    @click="handleLogout"
                  >
                    <Icon
                      name="arrow-right-from-bracket-light"
                      class="size-5"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGuidedTour } from "@/composables/useGuidedTour";
import { PAGE_TOUR_KEYS } from "@/tours/pageTourKeys";
import { computed, ref, watch } from "vue";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import Avatar from "@/components/Core/Avatar.vue";
import OrganizationSwitcherList from "@/components/Navigation/OrganizationSwitcherList.vue";
import Icon from "@/components/Core/Icon.vue";
import BetaBadge from "@/components/Core/BetaBadge.vue";
import { useAuthStore } from "@/store/auth";
import { useTrack } from "@/services/gtm";
import { useSidebarDock } from "@/composables/useSidebarDock";
import { useShellVisibility } from "@/composables/useShellVisibility";
import SidebarToggle from "@/components/Shell/SidebarToggle.vue";
import { useHasManagerCapability, useProfileQuery } from "@/services/user";
import {
  type LocationQueryRaw,
  RouteRecordRaw,
  useRoute,
  useRouter,
} from "vue-router";
import { visibleSettingsEntries } from "@/router/settings_menu";
import { navbarRoutes } from "@/router/sidenav_routes";
import { useSubmenuOverride } from "@/utils/submenuOverride";
import { useActiveOrganization } from "@/composables/useActiveOrganization";
import {
  useFeatureFlags,
  useFeatureFlagStatus,
} from "@/services/feature_flags";
import { toolsRoutes } from "@/router/tools_routes";
import { useWindowSize } from "@vueuse/core";
import { useFloating } from "@/utils/components";

const emit = defineEmits({
  "sidebar-width-change": (_width: number) => true,
});
const HELP_CENTER_URL = "https://community.fyr.ai/hc/en-us";

const authStore = useAuthStore();
const track = useTrack();
const self = useProfileQuery();
const currentRoute = useRoute();
const router = useRouter();
const { hasApplicationHeader } = useShellVisibility();
const { organizations, currentOrganization, selectOrganization } =
  useActiveOrganization();

// Hub gating follows the user's home agency (resolved server-side and gated
// on membership), not the active org selection — so the Hub link stays
// visible no matter which org is selected, as long as the user belongs to an
// agency.
const homeAgency = computed(() => self.data?.homeAgency ?? null);

// Belongs to at least one agency (controls whether the Hub slot applies).
const isCurrentAgencyUser = computed(() => homeAgency.value !== null);

const {
  reference: organizationSelectorReference,
  floating: organizationSelectorFloating,
  style: organizationSelectorStyle,
} = useFloating({ placement: "right-end", strategy: "fixed" });

const isSharedDashboard = computed(() => {
  return currentRoute.path.includes("shared-dashboard");
});

// Shared with the action bars, which host the collapse/expand toggle.
const { isExpanded } = useSidebarDock();
watch(isExpanded, (val) => {
  localStorage.setItem("sidebar-expanded", String(val));
});

// Multiple top-level menus can be open at once (e.g. CompMon and Analytics).
const openMenus = ref<Set<string>>(new Set());
function isMenuOpen(title: string | undefined): boolean {
  return title !== undefined && openMenus.value.has(title);
}

// Second-level folders track their own disclosure state, keyed by route name.
const openSubmenus = ref<Set<string>>(new Set());
function isSubmenuOpen(routeName: string): boolean {
  return openSubmenus.value.has(routeName);
}

// Outranks the route-driven default, so in-section navigation (tab bars,
// breadcrumbs, back/forward) can't reopen a folder you just closed. Expires
// when you leave the folder's subtree.
const manuallyClosedMenus = ref<Set<string>>(new Set());
const manuallyClosedSubmenus = ref<Set<string>>(new Set());

// The user asked for this one, so it clears any standing manual close.
function openMenu(menuTitle: string) {
  openMenus.value.add(menuTitle);
  manuallyClosedMenus.value.delete(menuTitle);
}

// Something else wants the folder open (route landed in it, a view registered
// an override). Never use openMenu() for these — it would wipe the manual
// close this is meant to respect.
function openMenuUnlessClosed(menuTitle: string) {
  if (!manuallyClosedMenus.value.has(menuTitle)) openMenus.value.add(menuTitle);
}

function toggleSubmenu(routeName: string) {
  if (openSubmenus.value.has(routeName)) {
    openSubmenus.value.delete(routeName);
    manuallyClosedSubmenus.value.add(routeName);
  } else {
    openSubmenus.value.add(routeName);
    manuallyClosedSubmenus.value.delete(routeName);
  }
}

const subNavItemClass =
  "font-arial flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left font-normal text-burgundy-600 hover:bg-burgundy-900/5 hover:text-burgundy-900";

// Mobile navigation state
const mobileMenuOpen = ref(false);
const mobileActiveMenu = ref<string | null>(null);
const mobileOrgSearchOpen = ref(false);
const mobileOrgSearchQuery = ref("");
const mobileFilteredOrganizations = computed(() => {
  const query = mobileOrgSearchQuery.value.toLowerCase().trim();
  if (!query) return organizations.data ?? [];
  return (
    organizations.data?.filter((org) =>
      org.name.toLowerCase().includes(query),
    ) ?? []
  );
});

// Close mobile menu on route change
watch(
  () => currentRoute.fullPath,
  () => {
    mobileMenuOpen.value = false;
  },
);
// Reset mobile org search when menu closes
watch(mobileMenuOpen, (open) => {
  if (!open) {
    mobileOrgSearchOpen.value = false;
    mobileOrgSearchQuery.value = "";
  }
});

// Nothing suppresses the override any more; folder clicks did, back when they
// navigated. isSuppressed is still read so the guards hold if that returns.
const {
  override: submenuOverride,
  isSuppressed: isOverrideSuppressed,
  wasSuppressedOnClear,
  clearOverride,
} = useSubmenuOverride();
const isManager = useHasManagerCapability();
const lastOverrideParentMenu = ref<string | null>(null);

// Has at least one flag-enabled member agency — the actual Hub-availability
// signal, matching the backend (which serves the hub if ANY member agency is
// enabled, not only the home agency).
const hasAgencyHub = computed(() => self.data?.hasAgencyHub === true);

// Generic nav gate for routes carrying meta.requiredFeatureFlag: hidden until
// the flags fetch resolves with the flag enabled (matches the route guard).
// Paywalled routes are the exception — they stay in the nav so an org can find
// what it isn't buying yet, and the route itself renders the pitch.
const featureFlags = useFeatureFlags();
function passesFeatureFlag(meta: RouteRecordRaw["meta"]): boolean {
  const flag = meta?.requiredFeatureFlag;
  if (!flag) return true;
  if (meta?.paywall) return true;
  return featureFlags.data?.[flag]?.enabled === true;
}

// Paywalled entries stay listed, but their Beta tag waits until the org can open them.
function hasFeatureAccess(meta: RouteRecordRaw["meta"]): boolean {
  const flag = meta?.requiredFeatureFlag;
  return !flag || featureFlags.data?.[flag]?.enabled === true;
}

// Route names that should be shown but disabled (greyed out with "In Beta").
// An agency member whose hub isn't enabled anywhere yet sees the greyed link.
// Budget Tool is deliberately absent: it's paywalled now, so the link stays
// enabled and navigates to its pitch instead of being greyed into a dead end.
// Greyed only when an agency member's Hub is switched off deliberately: the
// `agency_hub` flag now defaults on, so `hasAgencyHub` is false for an agency
// member only where an admin has killed the flag for that agency. Non-agency
// users don't see the entry at all (visibleNavbarRoutes below).
const disabledRouteNames = computed(() => {
  const names = new Set<string>();
  if (isCurrentAgencyUser.value && !hasAgencyHub.value) {
    names.add("hub");
  }
  return names;
});

// Hide nav items gated on org-level permissions (e.g., Agency Hub for non-agency users).
const visibleNavbarRoutes = computed(() =>
  navbarRoutes.filter((route) =>
    route.name === "hub" ? isCurrentAgencyUser.value : true,
  ),
);
const mobileNavbarRoutes = computed(() =>
  visibleNavbarRoutes.value.filter((route) => route.name !== "settings"),
);

const {
  isVisible: contentLabVisible,
  consentPending: contentLabConsentPending,
} = useFeatureFlagStatus("aiContentGeneration");

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const computedToolsRoutes = computed<RouteRecordRaw[]>(() =>
  toolsRoutes
    .filter((route) => {
      if (route.name === "create-content" && !contentLabVisible.value) {
        return false;
      }
      return true;
    })
    .map((route) => {
      if (route.name === "create-content" && contentLabConsentPending.value) {
        return {
          ...route,
          meta: { ...route.meta, consentPending: true },
        } as RouteRecordRaw;
      }
      return route;
    }),
);

const settingsTabs = computed(() =>
  // Membership and gating come from SETTINGS_MENU (router/settings_menu.ts);
  // this surface only picks the fuller nav labels and the icons.
  visibleSettingsEntries(isManager.value).map((entry) => ({
    name: entry.navName,
    href: entry.href,
    icon: entry.icon,
  })),
);

watch(
  () => submenuOverride.value,
  (override) => {
    if (override) {
      openMenuUnlessClosed(override.parentMenu);
      lastOverrideParentMenu.value = override.parentMenu;
    } else if (lastOverrideParentMenu.value && !wasSuppressedOnClear.value) {
      openMenuUnlessClosed(lastOverrideParentMenu.value);
    }
  },
  { immediate: true },
);

watch(
  () => currentRoute.name,
  (newRouteName) => {
    if (!submenuOverride.value || !newRouteName) return;
    const overrideRouteNames = submenuOverride.value.items.map(
      (item) => item.href,
    );
    const isWithinOverrideContext = overrideRouteNames.includes(
      String(newRouteName),
    );

    if (!isWithinOverrideContext) {
      clearOverride();
    }
  },
);
const { width: viewportWidth } = useWindowSize();
// Keep the default 192px up to 1920px-wide screens (the most common); only
// widen on larger displays, with extra room for ultrawides.
const expandedWidth = computed(() => {
  if (viewportWidth.value >= 3440) return 272;
  if (viewportWidth.value > 1920) return 240;
  return 208;
});
const sidebarVisualWidth = computed(() =>
  isExpanded.value ? expandedWidth.value : 56,
);
// Scale label text up one step alongside the width on larger displays,
// preserving the existing nav/submenu hierarchy.
const sidebarTier = computed(() =>
  viewportWidth.value >= 3440 ? 2 : viewportWidth.value > 1920 ? 1 : 0,
);
const navTextClass = computed(
  () => ["text-sm", "text-base", "text-lg"][sidebarTier.value],
);
const navSubTextClass = computed(
  () => ["text-[13px]", "text-sm", "text-base"][sidebarTier.value],
);
watch(
  sidebarVisualWidth,
  (width) => {
    emit("sidebar-width-change", width);
  },
  { immediate: true },
);

function toggleMenu(menuTitle: string | undefined) {
  if (menuTitle === undefined) return;
  if (openMenus.value.has(menuTitle)) {
    openMenus.value.delete(menuTitle);
    manuallyClosedMenus.value.add(menuTitle);
  } else {
    openMenu(menuTitle);
  }
}
function handleMenuClick(item: RouteRecordRaw) {
  if (item.meta?.isDirect) {
    router.push({ name: item.name });
    clearOverride();
    return;
  }

  const menuTitle = item.meta?.title as string;

  // Nothing to disclose, so the click has to navigate or it does nothing. Only
  // once flags have resolved: passesFeatureFlag() rejects everything while the
  // fetch is in flight, which would briefly navigate instead of disclosing.
  if (
    featureFlags.data !== undefined &&
    getAccordionChildren(menuTitle).length === 0
  ) {
    if (item.redirect !== undefined) router.push({ name: item.name });
    return;
  }

  // Never clear/suppress the override here: the view that registered it is
  // still mounted and only re-registers when its own page changes. The route
  // watcher drops it when navigation actually leaves it.

  // Collapsed, the submenu was never on screen, so the click reveals it rather
  // than toggling whatever the route watcher left open behind the rail.
  if (!isExpanded.value) {
    isExpanded.value = true;
    openMenu(menuTitle);
    return;
  }

  toggleMenu(menuTitle);
}

function handleHelpCenterClick() {
  track("click_help_center_navbar");
}

function selectFromMobileMenu(orgId: number): Promise<void> {
  mobileMenuOpen.value = false;
  return selectOrganization(orgId);
}

function handleLogout() {
  authStore.logout();
  router.push({ name: "login" });
}

function isParentActive(item: RouteRecordRaw): boolean {
  if (item.meta?.isDirect) {
    if (currentRoute.matched.some((m) => m.name === item.name)) return true;
    if (
      item.redirect &&
      typeof item.redirect === "object" &&
      "name" in item.redirect
    ) {
      const redirectTarget = item.redirect.name as string;
      if (
        currentRoute.name === redirectTarget ||
        currentRoute.matched.some((m) => m.name === redirectTarget)
      ) {
        return true;
      }
    }
    return false;
  }
  // Folders never carry the active highlight — the active child/leaf does, and a
  // folder being open (isMenuOpen) is independent of which view you're on.
  return false;
}

// Collapsed nav hides the submenu, so the folder icon itself must show the
// active state when the current view lives anywhere in its subtree.
function isFolderActive(item: RouteRecordRaw): boolean {
  if (item.meta?.isDirect) return false;
  const descendants = getDescendantNames(item);
  if (descendants.includes(String(currentRoute.name))) return true;
  return currentRoute.matched.some((m) => descendants.includes(String(m.name)));
}

// Collapsed rail: a purple text color alone is hard to read on the gray
// background, so the active icon also gets a filled background.
function isCollapsedActive(item: RouteRecordRaw): boolean {
  return !isExpanded.value && (isParentActive(item) || isFolderActive(item));
}

// Normally the open panel's active child carries the highlight. A folder you
// closed while standing inside it has no panel, so the row has to say so
// itself or the sidebar shows nothing about where you are.
function isClosedFolderActive(item: RouteRecordRaw): boolean {
  return (
    isExpanded.value && isFolderActive(item) && !isMenuOpen(item.meta?.title)
  );
}

// One source for the visible label, the tooltip and the accessible name:
// where they diverge, speech input stops matching what the user reads.
function navItemLabel(item: RouteRecordRaw): string {
  return String(item.meta?.text ?? item.meta?.title ?? item.name ?? "");
}

function getAccordionChildren(
  menuTitle: string,
): { name: string; href: string; beta?: boolean }[] {
  const route = navbarRoutes.find((r) => r.meta?.title === menuTitle);
  if (route?.name === "settings") return settingsTabs.value;
  if (!route?.children) return [];
  return route.children
    .filter(
      (child) => !child.meta?.excludeFromNavbar && !child.path.includes(":"),
    )
    .filter((child) => passesFeatureFlag(child.meta))
    .map((child) => ({
      name:
        (child.meta?.text as string) ??
        (child.meta?.title as string) ??
        String(child.name),
      href: String(child.name),
      beta: child.meta?.beta === true && hasFeatureAccess(child.meta),
    }));
}

function getSubChildren(
  routeName: string,
): { name: string; href: string; query?: LocationQueryRaw; beta?: boolean }[] {
  if (
    submenuOverride.value &&
    !isOverrideSuppressed.value &&
    isChildActive(routeName)
  ) {
    return submenuOverride.value.items;
  }
  for (const parentRoute of navbarRoutes) {
    const child = parentRoute.children?.find(
      (c) => String(c.name) === routeName,
    );
    if (child?.children) {
      const visible = child.children
        .filter(
          (sub) => !sub.path.includes(":") && !sub.meta?.excludeFromNavbar,
        )
        .filter((sub) => passesFeatureFlag(sub.meta))
        .map((sub) => ({
          name:
            (sub.meta?.text as string) ??
            (sub.meta?.title as string) ??
            String(sub.name),
          href: String(sub.name),
          beta: sub.meta?.beta === true && hasFeatureAccess(sub.meta),
        }));
      return visible.length > 1 ? visible : [];
    }
  }
  return [];
}

function getDescendantNames(route: RouteRecordRaw): string[] {
  const names: string[] = [];
  if (route.name) names.push(String(route.name));
  if (route.children) {
    for (const child of route.children) {
      names.push(...getDescendantNames(child));
    }
  }
  return names;
}

function isChildActive(routeName: string): boolean {
  if (currentRoute.matched.some((m) => m.name === routeName)) return true;
  for (const parentRoute of navbarRoutes) {
    const child = parentRoute.children?.find(
      (c) => String(c.name) === routeName,
    );
    if (child) {
      const descendants = getDescendantNames(child);
      if (descendants.includes(String(currentRoute.name))) return true;
      if (
        currentRoute.matched.some((m) => descendants.includes(String(m.name)))
      ) {
        return true;
      }
    }
  }
  return false;
}

function hasOverrideForChild(routeName: string): boolean {
  return !!(
    submenuOverride.value &&
    !isOverrideSuppressed.value &&
    isChildActive(routeName) &&
    submenuOverride.value.backLink
  );
}

watch(
  () => currentRoute.name,
  () => {
    if (submenuOverride.value && !isOverrideSuppressed.value) {
      return;
    }
    for (const item of navbarRoutes) {
      if (item.meta?.isDirect) continue;
      const menuTitle = item.meta?.title as string;
      // isFolderActive also covers a router sibling that is a navbarRoutes descendant.
      if (!isFolderActive(item)) {
        manuallyClosedMenus.value.delete(menuTitle);
        continue;
      }
      openMenuUnlessClosed(menuTitle);
    }
  },
  { immediate: true },
);

watch(
  () => currentRoute.name,
  () => {
    for (const parentRoute of navbarRoutes) {
      for (const child of parentRoute.children ?? []) {
        const href = String(child.name);
        if (!isChildActive(href)) {
          manuallyClosedSubmenus.value.delete(href);
          continue;
        }
        if (!manuallyClosedSubmenus.value.has(href)) {
          openSubmenus.value.add(href);
        }
      }
    }
  },
  { immediate: true },
);

const guidedTour = useGuidedTour();
const pageTourKey = computed(() => {
  const name = currentRoute.name;
  return typeof name === "string" ? PAGE_TOUR_KEYS[name] : undefined;
});
function startPageTourFromMenu() {
  if (!pageTourKey.value) return;
  mobileMenuOpen.value = false;
  guidedTour.start(pageTourKey.value);
}

const hasUnseenPageTour = computed(
  () =>
    !!pageTourKey.value &&
    guidedTour.isProgressLoaded() &&
    !guidedTour.hasSeen(pageTourKey.value),
);
</script>
